// ============================================================================
// PlaceholderReportScreen — Phase 1 shell shared by every report
// ============================================================================
// Wires the property + date range filter bar so each report screen has the
// exact UX it'll keep through Phase 5. Body shows a "Coming in Phase N" card
// until the matching phase fills in the grid + summary widgets.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import ReportFilterBar from 'components/common/ReportFilterBar';
import { useDateRangePreset } from 'hooks/useDateRangePreset';
import 'components/modules/dashboard-module/dashboard.css';

interface Props {
  title: string;
  description: string;
  icon?: React.ReactNode;
  /** Phase label shown on the placeholder card, e.g. "Coming in Phase 2". */
  phaseLabel: string;
  /** Optional one-liner explaining what the report will show. */
  comingSoonHint?: string;
}

const PlaceholderReportScreen: React.FC<Props> = ({
  title, description, icon, phaseLabel, comingSoonHint,
}) => {
  const [propertyId, setPropertyId] = React.useState<string>('');
  const dateRange = useDateRangePreset('MTD');

  return (
    <div className="module-master-container">
      <ModuleHeader
        title={title}
        description={description}
        icon={icon ?? <IconSystem.file size={18} />}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
          <ReportFilterBar
            propertyId={propertyId}
            onPropertyChange={setPropertyId}
            preset={dateRange.value.preset}
            from={dateRange.value.from}
            to={dateRange.value.to}
            onPresetChange={dateRange.setPreset}
            onFromChange={dateRange.setCustomFrom}
            onToChange={dateRange.setCustomTo}
          />

          <div className="coming-soon-banner" style={{ marginTop: '0.5rem' }}>
            <div className="coming-soon-banner-title">{phaseLabel}</div>
            {comingSoonHint && (
              <div className="coming-soon-banner-meta">{comingSoonHint}</div>
            )}
            <div className="coming-soon-banner-meta">
              {propertyId
                ? `Filter ready: ${dateRange.value.from} → ${dateRange.value.to}`
                : 'Pick a property to enable refresh.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceholderReportScreen;
